/**
 * Open state for a single RmaListFilterControl popover.
 * Exclusivity is derived from the id held by RmaListFilterPopoverGroup: a control is open only
 * while it owns that id, so opening one closes the others without cross-component callbacks.
 * Falls back to local state when rendered outside a group.
 */
import { useCallback, useContext, useId, useState } from "react";
import { RmaListFilterPopoverGroupContext } from "./rma-list-filter-popover-group";

interface IUseRmaListFilterPopoverOptions {
    disabled?: boolean;
    onOpen?: () => void;
}

interface IUseRmaListFilterPopoverResult {
    isOpen: boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;
}

export const useRmaListFilterPopover = ({
    disabled,
    onOpen,
}: IUseRmaListFilterPopoverOptions = {}): IUseRmaListFilterPopoverResult => {
    const filterId = useId();
    const localState = useState<string | null>(null);
    const [openFilterId, setOpenFilterId] = useContext(RmaListFilterPopoverGroupContext) ?? localState;
    const isOpen = openFilterId === filterId;

    // Kept stable: the portal uses it as a dependency of its scroll-dismiss listener effect.
    const close = useCallback(() => {
        setOpenFilterId((current) => (current === filterId ? null : current));
    }, [filterId, setOpenFilterId,]);

    const open = () => {
        if (disabled) {
            return;
        }
        setOpenFilterId(filterId);
        onOpen?.();
    };

    const toggle = () => {
        if (isOpen) {
            close();
            return;
        }
        open();
    };

    return {
        isOpen,
        open,
        close,
        toggle,
    };
};
